import React, { useRef } from "react";
import { FileText, Calendar, HelpCircle } from "lucide-react";
import { Input } from "@shared/ui/input";
import { GenericSelect } from "@shared/components/Select";
import { cn } from "@shared/lib/utils";

import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useBillConfigMeta } from "@features/cx/disconnect/hooks";

interface InvoiceDetailsProps {
  config: {
    startDate: string;
    endDate: string;
    paymentDueDays: number;
    template: string;
    templateSource: string;
  };
  onChange: (config: any) => void;
  disabled?: boolean;
}

export const InvoiceDetails = ({
  config,
  onChange,
  disabled,
}: InvoiceDetailsProps) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: billConfigMeta } = useBillConfigMeta(Number(remoteUtilityId));

  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  const templates =
    billConfigMeta?.map((item: any) => ({
      label: item.configName,
      value: item.id,
    })) || [];

  const handleFieldChange = (field: string, value: any) => {
    if (disabled) return;
    onChange({ ...config, [field]: value });
  };

  const openPicker = (ref: React.RefObject<HTMLInputElement>) => {
    if (disabled) return;
    try {
      // Modern way to open native pickers
      if (ref.current && "showPicker" in HTMLInputElement.prototype) {
        ref.current.showPicker();
      } else {
        ref.current?.focus();
      }
    } catch (error) {
      ref.current?.focus();
    }
  };

  return (
    <div
      className={cn(
        "bg-white p-6 rounded-xl shadow-sm border border-neutral-100 transition-opacity",
        disabled && "opacity-80",
      )}
    >
      <div className="flex items-center gap-2 mb-6">
        <div
          className={cn(
            "p-2 rounded-lg",
            disabled ? "bg-neutral-100" : "bg-blue-50",
          )}
        >
          <FileText
            className={cn(
              "w-5 h-5",
              disabled ? "text-neutral-400" : "text-blue-600",
            )}
          />
        </div>
        <h2
          className={cn(
            "text-lg font-bold",
            disabled ? "text-neutral-500" : "text-neutral-800",
          )}
        >
          Invoice Details{" "}
          {disabled && (
            <span className="text-xs font-normal ml-2">(Read-only)</span>
          )}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Start Date */}
        <div className="space-y-2">
          <label
            className={cn(
              "text-xs font-bold  tracking-wider flex items-center gap-1",
              disabled ? "text-neutral-400" : "text-neutral-500",
            )}
          >
            Start Date {!disabled && <span className="text-red-500">*</span>}
          </label>
          <div
            className="relative group cursor-pointer"
            onClick={() => openPicker(startDateRef)}
          >
            <Input
              ref={startDateRef}
              type="date"
              value={config.startDate}
              disabled={disabled}
              onChange={(e) => handleFieldChange("startDate", e.target.value)}
              className="h-12 border-neutral-200 focus:border-blue-500 rounded-lg pr-10 disabled:bg-neutral-50 disabled:text-neutral-500 [appearance:none] [&::-webkit-calendar-picker-indicator]:hidden cursor-pointer"
            />
            <Calendar className="w-4 h-4 text-neutral-400 absolute right-3 top-4 group-hover:text-blue-500 transition-colors" />
          </div>
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <label
            className={cn(
              "text-xs font-bold  tracking-wider flex items-center gap-1",
              disabled ? "text-neutral-400" : "text-neutral-500",
            )}
          >
            End Date {!disabled && <span className="text-red-500">*</span>}
          </label>
          <div
            className="relative group cursor-pointer"
            onClick={() => openPicker(endDateRef)}
          >
            <Input
              ref={endDateRef}
              type="date"
              value={config.endDate}
              disabled={disabled}
              onChange={(e) => handleFieldChange("endDate", e.target.value)}
              className="h-12 border-neutral-200 focus:border-blue-500 rounded-lg pr-10 disabled:bg-neutral-50 disabled:text-neutral-500 [appearance:none] [&::-webkit-calendar-picker-indicator]:hidden cursor-pointer"
            />
            <Calendar className="w-4 h-4 text-neutral-400 absolute right-3 top-4 group-hover:text-blue-500 transition-colors" />
          </div>
        </div>

        {/* Payment Due */}
        <div className="space-y-2">
          <label
            className={cn(
              "text-xs font-bold  tracking-wider flex items-center gap-1",
              disabled ? "text-neutral-400" : "text-neutral-500",
            )}
          >
            Payment Due in
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={config.paymentDueDays}
              disabled={disabled}
              placeholder="10"
              onChange={(e) =>
                handleFieldChange(
                  "paymentDueDays",
                  parseInt(e.target.value) || 0,
                )
              }
              className="h-12 border-neutral-200 focus:border-blue-500 rounded-lg disabled:bg-neutral-50 disabled:text-neutral-500 flex-1 min-w-0"
            />
            <span className="text-sm text-neutral-400 font-bold whitespace-nowrap">
              Days
            </span>
          </div>
        </div>

        {/* Template Selection */}
        <div className="space-y-2">
          <label
            className={cn(
              "text-xs font-bold  tracking-wider flex items-center gap-1",
              disabled ? "text-neutral-400" : "text-neutral-500",
            )}
          >
            Select Template{" "}
            {!disabled && <span className="text-red-500">*</span>}
          </label>
          <GenericSelect
            value={config.template}
            disabled={disabled}
            onChange={(val) => {
              const selected = billConfigMeta?.find(
                (item: any) => item.id.toString() === val.toString(),
              );
              onChange({
                ...config,
                template: val,
                templateSource: selected?.source || "",
              });
            }}
            options={templates}
            placeholder="Select Template"
            className="h-12 border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-400"
          />
        </div>
      </div>
    </div>
  );
};
